import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemButton,

  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { GoogleMap, useJsApiLoader, Polygon, Marker } from '@react-google-maps/api';
import {
  useGetLocationsQuery,
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useDeleteLocationMutation
} from '../../../redux/adminApiSlice';
import { Location, LocationType } from '../../../types/interfaces';
import { DARK_MAP_STYLE } from '../../../components/Map/MapComponent';

const mapContainerStyle = {
  width: '100%',
  height: '100%',
  minHeight: '600px'
};

// Default center on Manhattan
const defaultCenter = {
  lat: 40.7831,
  lng: -73.9712
};

const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: true,
  fullscreenControl: true,
  zoom: 12,
  mapTypeId: 'roadmap' as google.maps.MapTypeId,
  // Disable clickable POIs (Points of Interest) like subway stations, businesses, etc.
  clickableIcons: false,
  // Dark theme + custom styles to hide/disable map elements that could interfere.
  styles: [
    ...DARK_MAP_STYLE,
    {
      featureType: "poi",
      elementType: "labels",
      stylers: [{ visibility: "off" }]
    },
    {
      featureType: "poi.business",
      stylers: [{ visibility: "off" }]
    },
    {
      featureType: "transit",
      elementType: "labels.icon",
      stylers: [{ visibility: "off" }]
    },
    {
      featureType: "transit.station",
      stylers: [{ visibility: "off" }]
    },
    {
      featureType: "transit.line",
      stylers: [{ visibility: "off" }]
    }
  ]
};

interface NeighborhoodEditorProps {}

const NeighborhoodEditor: React.FC<NeighborhoodEditorProps> = () => {
  // Map and Google Maps API
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''
  });

  // State

  const [selectedState, setSelectedState] = useState<string>('');
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [selectedBorough, setSelectedBorough] = useState<string>('');
  const [neighborhoods, setNeighborhoods] = useState<Location[]>([]);
  const [selectedNeighborhood, setSelectedNeighborhood] = useState<Location | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [newNeighborhoodName, setNewNeighborhoodName] = useState('');
  const [newNeighborhoodBorough, setNewNeighborhoodBorough] = useState<string>('');
  const [adminBoundingBox, setAdminBoundingBox] = useState(false);
  const [drawingPath, setDrawingPath] = useState<google.maps.LatLngLiteral[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [editingPath, setEditingPath] = useState<google.maps.LatLngLiteral[]>([]);
  const [snapPoint, setSnapPoint] = useState<google.maps.LatLngLiteral | null>(null);
  const [isNearSnapPoint, setIsNearSnapPoint] = useState(false);
  const [customCenter, setCustomCenter] = useState<google.maps.LatLngLiteral | null>(null);
  const [isDraggingCenter, setIsDraggingCenter] = useState(false);

  const [visibleNeighborhoods, setVisibleNeighborhoods] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [neighborhoodToDelete, setNeighborhoodToDelete] = useState<Location | null>(null);
  const [showOnlyNonAdminApproved, setShowOnlyNonAdminApproved] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Refs
  const neighborhoodListRef = useRef<HTMLUListElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  // RTK Query hooks
  const { data: locations = [], refetch } = useGetLocationsQuery();
  const [createLocation] = useCreateLocationMutation();
  const [updateLocation] = useUpdateLocationMutation();
  const [deleteLocation] = useDeleteLocationMutation();

  // Get states for dropdown (alphabetized) with city counts
  const states = locations
    .filter(loc => loc.level === LocationType.STATE || loc.level === 'State')
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(state => {
      const cityCount = locations.filter(loc => 
        (loc.level === LocationType.CITY || loc.level === 'City') && 
        loc.parent === state._id
      ).length;
      return { ...state, childCount: cityCount };
    });

  // Get cities for dropdown (filtered by selected state and alphabetized) with neighborhood counts
  const cities = locations
    .filter(loc => {
      const isCity = loc.level === LocationType.CITY || loc.level === 'City';
      const matchesState = selectedState ? loc.parent === selectedState : true;
      return isCity && matchesState;
    })
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(city => {
      const neighborhoodCount = locations.filter(loc => 
        (loc.level === LocationType.NEIGHBORHOOD || loc.level === 'Neighborhood') && 
        loc.parent === city._id
      ).length;
      return { ...city, childCount: neighborhoodCount };
    });

  // NYC Boroughs
  const boroughs = ['Manhattan', 'Brooklyn', 'Queens', 'The Bronx', 'Staten Island'];

  // Zoom map to location based on bounding box or coordinates
  const zoomToLocation = useCallback((location: Location) => {
    if (!mapRef.current) return;

    try {
      if (location.boundingBox && location.boundingBox.coordinates) {
        // Use bounding box if available
        const bounds = new google.maps.LatLngBounds();
        const coordinates = location.boundingBox.coordinates[0]; // Get outer ring
        
        coordinates.forEach(coord => {
          bounds.extend(new google.maps.LatLng(coord[1], coord[0])); // lat, lng
        });
        
        mapRef.current.fitBounds(bounds);
        console.log(`[NeighborhoodEditor] Zoomed to ${location.name} using bounding box`);
      } else if (location.coordinates && location.coordinates.coordinates) {
        // Use center point with appropriate zoom level
        const [lng, lat] = location.coordinates.coordinates;
        const center = { lat, lng };
        
        // Set zoom level based on location level
        let zoomLevel = 10; // Default
        if (location.level === 'State') {
          zoomLevel = 6;
        } else if (location.level === 'City') {
          zoomLevel = 11;
        }
        
        mapRef.current.setCenter(center);
        mapRef.current.setZoom(zoomLevel);
        console.log(`[NeighborhoodEditor] Zoomed to ${location.name} using center point (zoom: ${zoomLevel})`);
      } else {
        console.warn(`[NeighborhoodEditor] No geographic data available for ${location.name}`);
      }
    } catch (error) {
      console.error(`[NeighborhoodEditor] Error zooming to ${location.name}:`, error);
    }
  }, []);

  // Clear city selection when state changes and zoom to state
  useEffect(() => {
    if (selectedState) {
      setSelectedCity('');
      setSelectedBorough('');
      
      // Zoom to selected state
      const state = locations.find(loc => loc._id === selectedState);
      if (state) {
        zoomToLocation(state);
      }
    }
  }, [selectedState, locations, zoomToLocation]);

  // Zoom to selected city
  useEffect(() => {
    if (selectedCity) {
      const city = locations.find(loc => loc._id === selectedCity);
      if (city) {
        zoomToLocation(city);
      }
    }
  }, [selectedCity, locations, zoomToLocation]);

  // Get neighborhoods for selected city and borough
  useEffect(() => {
    if (selectedCity) {
      let cityNeighborhoods = locations.filter(loc => 
        (loc.level === LocationType.NEIGHBORHOOD || loc.level === 'Neighborhood') &&
        loc.parent === selectedCity
      );

      // Filter by borough if selected
      if (selectedBorough) {
        cityNeighborhoods = cityNeighborhoods.filter(loc => loc.borough === selectedBorough);
      }

      // Filter by admin approval status if enabled
      if (showOnlyNonAdminApproved) {
        cityNeighborhoods = cityNeighborhoods.filter(loc => !loc.adminBoundingBox);
      }

      // Sort neighborhoods alphabetically by name
      cityNeighborhoods.sort((a, b) => a.name.localeCompare(b.name));

      setNeighborhoods(cityNeighborhoods);
    } else {
      setNeighborhoods([]);
    }
  }, [selectedCity, selectedBorough, showOnlyNonAdminApproved, locations]);

  // Scroll to selected neighborhood in the list
  const scrollToNeighborhood = useCallback((neighborhoodId: string) => {
    if (!neighborhoodListRef.current) return;
    
    const neighborhoodElement = neighborhoodListRef.current.querySelector(`[data-neighborhood-id="${neighborhoodId}"]`);
    if (neighborhoodElement) {
      neighborhoodElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, []);

  // Auto-scroll to selected neighborhood when it changes (from map clicks)
  useEffect(() => {
    if (selectedNeighborhood && neighborhoods.some(n => n._id === selectedNeighborhood._id)) {
      // Small delay to ensure the list has rendered
      setTimeout(() => {
        scrollToNeighborhood(selectedNeighborhood._id);
      }, 100);
    }
  }, [selectedNeighborhood, neighborhoods, scrollToNeighborhood]);

  // Map event handlers
  const onLoad = useCallback((map: google.maps.Map) => {
    // Store map reference for zooming functionality
    mapRef.current = map;
    console.log('[NeighborhoodEditor] Map loaded and reference stored');
  }, []);

  const onUnmount = useCallback(() => {
    // Clear map reference when unmounting
    mapRef.current = null;
    console.log('[NeighborhoodEditor] Map unmounted and reference cleared');
  }, []);

  // Snap-to-existing-points functionality
  const getAllExistingPoints = useCallback((): google.maps.LatLngLiteral[] => {
    const points: google.maps.LatLngLiteral[] = [];
    
    // Get points from all visible neighborhoods (except the one being edited)
    locations
      .filter(loc => 
        (loc.level === LocationType.NEIGHBORHOOD || loc.level === 'Neighborhood') &&
        loc.boundingBox?.coordinates?.[0] &&
        visibleNeighborhoods.has(loc._id) &&
        loc._id !== selectedNeighborhood?._id // Exclude current neighborhood
      )
      .forEach(neighborhood => {
        const coords = neighborhood.boundingBox!.coordinates![0];
        coords.forEach(([lng, lat]) => {
          points.push({ lat, lng });
        });
      });
    
    return points;
  }, [locations, visibleNeighborhoods, selectedNeighborhood]);

  const findNearestSnapPoint = useCallback((mouseLatLng: google.maps.LatLngLiteral): google.maps.LatLngLiteral | null => {
    const existingPoints = getAllExistingPoints();
    const SNAP_DISTANCE_DEGREES = 0.0001; // Approximately 10-15 meters
    
    let nearestPoint: google.maps.LatLngLiteral | null = null;
    let minDistance = Infinity;

    existingPoints.forEach(point => {
      // Calculate geographic distance (simplified)
      const latDiff = mouseLatLng.lat - point.lat;
      const lngDiff = mouseLatLng.lng - point.lng;
      const distance = Math.sqrt(latDiff * latDiff + lngDiff * lngDiff);

      if (distance < SNAP_DISTANCE_DEGREES && distance < minDistance) {
        minDistance = distance;
        nearestPoint = point;
      }
    });

    return nearestPoint;
  }, [getAllExistingPoints]);

  const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {
    if (!event.latLng) return;
    
    // Allow drawing new points when in drawing mode
    if (isDrawing) {
      let pointToAdd = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      };

      // Use snap point if we're near one
      if (snapPoint && isNearSnapPoint) {
        pointToAdd = snapPoint;
      }

      setDrawingPath(prev => [...prev, pointToAdd]);
      
      // Clear snap indicators after adding point
      setSnapPoint(null);
      setIsNearSnapPoint(false);
    }
  }, [isDrawing, snapPoint, isNearSnapPoint]);

  const handleMapMouseMove = useCallback((event: google.maps.MapMouseEvent) => {
    if (!isDrawing || !event.latLng) return;

    const mouseLatLng = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng()
    };

    // Find nearest snap point
    const nearestPoint = findNearestSnapPoint(mouseLatLng);

    if (nearestPoint) {
      setSnapPoint(nearestPoint);
      setIsNearSnapPoint(true);
    } else {
      setSnapPoint(null);
      setIsNearSnapPoint(false);
    }
  }, [isDrawing, findNearestSnapPoint]);

  // Neighborhood management functions
  const startCreating = () => {
    if (!selectedCity) {
      setError('Please select a city first');
      return;
    }
    setIsCreating(true);
    setIsEditing(false);
    setSelectedNeighborhood(null);
    setNewNeighborhoodName('');
    setNewNeighborhoodBorough(selectedBorough || 'Manhattan');
    setAdminBoundingBox(true); // Default to true for new neighborhoods created by admin
    setDrawingPath([]);
    setError(null);
  };

  const startEditing = (neighborhood: Location) => {
    setSelectedNeighborhood(neighborhood);
    setIsEditing(true);
    setIsCreating(false);
    setNewNeighborhoodName(neighborhood.name);
    setNewNeighborhoodBorough(neighborhood.borough || 'Manhattan');
    setAdminBoundingBox(neighborhood.adminBoundingBox || false);
    
    // Load existing bounds if available for editing
    if (neighborhood.boundingBox?.coordinates?.[0]) {
      const coords = neighborhood.boundingBox.coordinates[0];
      // Remove the last point (duplicate of first) for editing
      const editPath = coords.slice(0, -1).map(([lng, lat]) => ({ lat, lng }));
      setEditingPath(editPath);
    } else {
      setEditingPath([]);
    }
    
    // Load existing center point as custom center for dragging
    if (neighborhood.coordinates?.coordinates) {
      const [lng, lat] = neighborhood.coordinates.coordinates;
      setCustomCenter({ lat, lng });
    } else {
      setCustomCenter(null);
    }
    
    // Clear drawing path when editing existing bounds
    setDrawingPath([]);
    setError(null);
  };

  const startDrawing = () => {
    setIsDrawing(true);
    setDrawingPath([]);
    setError(null);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    setSnapPoint(null);
    setIsNearSnapPoint(false);
  };

  const clearDrawing = () => {
    setDrawingPath([]);
  };

  const recalculateCenter = () => {
    // Use editing path if available, otherwise use drawing path
    const currentPath = isEditing && editingPath.length > 0 ? editingPath : drawingPath;
    
    if (currentPath.length < 3) {
      setError('Need at least 3 points to calculate center');
      return;
    }

    // Calculate new center point
    const centerLat = currentPath.reduce((sum, point) => sum + point.lat, 0) / currentPath.length;
    const centerLng = currentPath.reduce((sum, point) => sum + point.lng, 0) / currentPath.length;

    // Set as custom center so it can be dragged
    setCustomCenter({ lat: centerLat, lng: centerLng });

    console.log(`[NeighborhoodEditor] Recalculated center: ${centerLat}, ${centerLng}`);
    setSuccess(`Center recalculated: ${centerLat.toFixed(6)}, ${centerLng.toFixed(6)} (now draggable)`);
    
    // Clear any existing errors
    setError(null);
  };

  const handleCenterDragStart = () => {
    setIsDraggingCenter(true);
  };

  const handleCenterDragEnd = (event: google.maps.MapMouseEvent) => {
    setIsDraggingCenter(false);
    if (event.latLng) {
      const newCenter = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      };
      setCustomCenter(newCenter);
      setSuccess(`Center moved to: ${newCenter.lat.toFixed(6)}, ${newCenter.lng.toFixed(6)}`);
    }
  };

  const resetCenter = () => {
    setCustomCenter(null);
    setSuccess('Center reset to auto-calculated position');
  };

  // Handle dragging points when editing existing bounds
  const handlePointDragStart = () => {
    // Could be used for visual feedback during drag
  };

  const handlePointDragEnd = (index: number, newPosition: google.maps.LatLngLiteral) => {
    const updatedPath = [...editingPath];
    updatedPath[index] = newPosition;
    setEditingPath(updatedPath);
  };



  const removePointFromEditingPath = (index: number) => {
    if (editingPath.length <= 3) {
      setError('Cannot remove point - minimum 3 points required');
      return;
    }
    const updatedPath = editingPath.filter((_, i) => i !== index);
    setEditingPath(updatedPath);
  };



  const addPointToEditingPath = (event: google.maps.PolyMouseEvent) => {
    if (!event.latLng) return;
    
    const newPoint = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng()
    };
    
    // Find the best position to insert the new point
    // We'll insert it at the end for now, but ideally we'd find the closest edge
    const insertIndex = findBestInsertIndex(newPoint, editingPath);
    const updatedPath = [...editingPath];
    updatedPath.splice(insertIndex, 0, newPoint);
    setEditingPath(updatedPath);
  };

  // Helper function to find the best position to insert a new point
  const findBestInsertIndex = (newPoint: google.maps.LatLngLiteral, path: google.maps.LatLngLiteral[]): number => {
    if (path.length < 2) return path.length;
    
    let minDistance = Infinity;
    let bestIndex = path.length;
    
    // Check distance to each edge and find the closest one
    for (let i = 0; i < path.length; i++) {
      const nextIndex = (i + 1) % path.length;
      const edgeStart = path[i];
      const edgeEnd = path[nextIndex];
      
      // Calculate distance from point to line segment
      const distance = distanceToLineSegment(newPoint, edgeStart, edgeEnd);
      
      if (distance < minDistance) {
        minDistance = distance;
        bestIndex = nextIndex; // Insert after the start of the closest edge
      }
    }
    
    return bestIndex;
  };

  // Helper function to calculate distance from point to line segment
  const distanceToLineSegment = (
    point: google.maps.LatLngLiteral,
    lineStart: google.maps.LatLngLiteral,
    lineEnd: google.maps.LatLngLiteral
  ): number => {
    const A = point.lat - lineStart.lat;
    const B = point.lng - lineStart.lng;
    const C = lineEnd.lat - lineStart.lat;
    const D = lineEnd.lng - lineStart.lng;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    
    if (lenSq === 0) return Math.sqrt(A * A + B * B);
    
    let param = dot / lenSq;
    param = Math.max(0, Math.min(1, param));
    
    const xx = lineStart.lat + param * C;
    const yy = lineStart.lng + param * D;
    
    const dx = point.lat - xx;
    const dy = point.lng - yy;
    
    return Math.sqrt(dx * dx + dy * dy);
  };

  const saveNeighborhood = async () => {
    if (!newNeighborhoodName.trim()) {
      setError('Please enter a neighborhood name');
      return;
    }

    // Use editing path if available, otherwise use drawing path
    const currentPath = isEditing && editingPath.length > 0 ? editingPath : drawingPath;
    
    if (currentPath.length < 3) {
      setError('Please draw at least 3 points to create a boundary');
      return;
    }

    try {
      // Ensure the polygon is closed
      const closedPath = [...currentPath];
      if (currentPath.length >= 3 && 
          JSON.stringify(currentPath[0]) !== JSON.stringify(currentPath[currentPath.length - 1])) {
        closedPath.push({ ...currentPath[0] });
      }

      // Calculate bounding box (southWest and northEast corners)
      const lats = currentPath.map(point => point.lat);
      const lngs = currentPath.map(point => point.lng);
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);

      const boundingBox = {
        southWest: { lng: minLng, lat: minLat },
        northEast: { lng: maxLng, lat: maxLat }
      };

      // Use custom center if set, otherwise calculate from boundary points
      let centerLat, centerLng;
      if (customCenter) {
        centerLat = customCenter.lat;
        centerLng = customCenter.lng;
      } else {
        centerLat = currentPath.reduce((sum, point) => sum + point.lat, 0) / currentPath.length;
        centerLng = currentPath.reduce((sum, point) => sum + point.lng, 0) / currentPath.length;
      }

      const center = {
        lng: centerLng,
        lat: centerLat
      };

      if (isCreating) {
        // Create new neighborhood
        await createLocation({
          name: newNeighborhoodName.trim(),
          level: 'Neighborhood',
          parent: selectedCity,
          boundingBox,
          center,
          adminBoundingBox,
          borough: newNeighborhoodBorough
        }).unwrap();

        setSuccess('Neighborhood created successfully');
      } else if (isEditing && selectedNeighborhood) {
        // Update existing neighborhood
        await updateLocation({
          id: selectedNeighborhood._id,
          updates: {
            name: newNeighborhoodName.trim(),
            boundingBox: {
              type: 'Polygon' as const,
              coordinates: [[
                ...closedPath.map(point => [point.lng, point.lat])
              ]]
            },
            coordinates: {
              type: 'Point' as const,
              coordinates: [centerLng, centerLat]
            },
            adminBoundingBox,
            borough: newNeighborhoodBorough
          }
        }).unwrap();

        setSuccess('Neighborhood updated successfully');
      }

      // Reset state
      setIsCreating(false);
      setIsEditing(false);
      setSelectedNeighborhood(null);
      setNewNeighborhoodName('');
      setDrawingPath([]);
      setIsDrawing(false);
      setError(null);

      // Refetch data
      refetch();
    } catch (error: any) {
      console.error('[NeighborhoodEditor] Error saving neighborhood:', error);
      setError(error.data?.message || 'Failed to save neighborhood');
    }
  };

  const cancelEditing = () => {
    setIsCreating(false);
    setIsEditing(false);
    setSelectedNeighborhood(null);
    setNewNeighborhoodName('');
    setNewNeighborhoodBorough('');
    setAdminBoundingBox(false);
    setDrawingPath([]);
    setEditingPath([]);
    setIsDrawing(false);
    setCustomCenter(null);
    setError(null);
  };

  const confirmDelete = (neighborhood: Location) => {
    setNeighborhoodToDelete(neighborhood);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!neighborhoodToDelete) return;

    try {
      await deleteLocation({
        id: neighborhoodToDelete._id,
        level: neighborhoodToDelete.level
      }).unwrap();

      setSuccess('Neighborhood deleted successfully');
      setShowDeleteDialog(false);
      setNeighborhoodToDelete(null);
      refetch();
    } catch (error: any) {
      console.error('[NeighborhoodEditor] Error deleting neighborhood:', error);
      setError(error.data?.message || 'Failed to delete neighborhood');
    }
  };

  const toggleNeighborhoodVisibility = (neighborhoodId: string) => {
    setVisibleNeighborhoods(prev => {
      const newSet = new Set(prev);
      if (newSet.has(neighborhoodId)) {
        newSet.delete(neighborhoodId);
      } else {
        newSet.add(neighborhoodId);
      }
      return newSet;
    });
  };

  const showAllNeighborhoods = () => {
    setVisibleNeighborhoods(new Set(neighborhoods.map(n => n._id)));
  };

  const hideAllNeighborhoods = () => {
    setVisibleNeighborhoods(new Set());
  };

  const showAllApproved = () => {
    // Get all neighborhoods from the full locations list that have admin-approved bounding boxes
    const approvedNeighborhoods = locations.filter(loc => 
      (loc.level === LocationType.NEIGHBORHOOD || loc.level === 'Neighborhood') &&
      loc.adminBoundingBox === true &&
      loc.boundingBox?.coordinates?.[0] // Make sure they actually have bounds to display
    );
    
    // If borough filter is active, also filter by borough
    const filteredApproved = selectedBorough 
      ? approvedNeighborhoods.filter(loc => loc.borough === selectedBorough)
      : approvedNeighborhoods;
    
    setVisibleNeighborhoods(new Set(filteredApproved.map(n => n._id)));
  };

  // Search for neighborhoods and auto-select city
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    
    if (query.trim().length < 2) {
      return; // Don't search for very short queries
    }

    // Find neighborhoods that match the search query
    const matchingNeighborhoods = locations.filter(loc =>
      (loc.level === LocationType.NEIGHBORHOOD || loc.level === 'Neighborhood') &&
      loc.name.toLowerCase().includes(query.toLowerCase())
    );

    if (matchingNeighborhoods.length > 0) {
      // Get the first match and find its parent city
      const firstMatch = matchingNeighborhoods[0];
      const parentCity = locations.find(loc => loc._id === firstMatch.parent);
      
      if (parentCity) {
        // Find the parent state
        const parentState = locations.find(loc => loc._id === parentCity.parent);
        
        if (parentState) {
          // Auto-select the state, city and borough
          setSelectedState(parentState._id);
        }
        
        // Auto-select the city and borough
        setSelectedCity(parentCity._id);
        if (firstMatch.borough) {
          setSelectedBorough(firstMatch.borough);
        }
        
        // Clear other filters to ensure the neighborhood is visible
        setShowOnlyNonAdminApproved(false);
        
        // Highlight the found neighborhood
        setTimeout(() => {
          setSelectedNeighborhood(firstMatch);
          setVisibleNeighborhoods(new Set([firstMatch._id]));
        }, 100);
      }
    }
  };

  if (!isLoaded) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading Google Maps...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex' }}>
      {/* Left Sidebar */}
      <Paper sx={{ width: 350, p: 2, overflow: 'auto' }}>
        <Typography variant="h6" gutterBottom>
          Neighborhood Editor
        </Typography>

        {/* Search Field */}
        <TextField
          fullWidth
          label="Search Neighborhoods"
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Type to search..."
          size="small"
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <Box sx={{ mr: 1, color: 'text.secondary' }}>
                🔍
              </Box>
            ),
            endAdornment: searchQuery && (
              <IconButton
                size="small"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedNeighborhood(null);
                }}
                sx={{ mr: -1 }}
              >
                ✕
              </IconButton>
            )
          }}
        />

        {/* Search Results Indicator */}
        {searchQuery && searchQuery.length >= 2 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {(() => {
              const matches = locations.filter(loc =>
                (loc.level === LocationType.NEIGHBORHOOD || loc.level === 'Neighborhood') &&
                loc.name.toLowerCase().includes(searchQuery.toLowerCase())
              );
              return matches.length > 0 
                ? `Found ${matches.length} neighborhood${matches.length > 1 ? 's' : ''} matching "${searchQuery}"`
                : `No neighborhoods found matching "${searchQuery}"`;
            })()}
          </Typography>
        )}

        {/* State Selection */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Select State</InputLabel>
          <Select
            value={selectedState}
            onChange={(e) => setSelectedState(e.target.value)}
            label="Select State"
          >
            <MenuItem value="">
              <em>All States</em>
            </MenuItem>
            {states.map((state) => (
              <MenuItem key={state._id} value={state._id}>
                {state.name} ({state.childCount})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* City Selection */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Select City</InputLabel>
          <Select
            value={selectedCity}
            onChange={(e) => setSelectedCity(e.target.value)}
            label="Select City"
            disabled={!selectedState && states.length > 0}
          >
            <MenuItem value="">
              <em>{selectedState ? 'Select City' : 'Select State First'}</em>
            </MenuItem>
            {cities.map((city) => (
              <MenuItem key={city._id} value={city._id}>
                {city.name} ({city.childCount})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Borough Selection */}
        {selectedCity && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Filter by Borough</InputLabel>
            <Select
              value={selectedBorough}
              onChange={(e) => setSelectedBorough(e.target.value)}
              label="Filter by Borough"
            >
              <MenuItem value="">All Boroughs</MenuItem>
              {boroughs.map((borough) => (
                <MenuItem key={borough} value={borough}>
                  {borough}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Filter Options */}
        {selectedCity && (
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showOnlyNonAdminApproved}
                  onChange={(e) => setShowOnlyNonAdminApproved(e.target.checked)}
                />
              }
              label="Show only non-admin approved"
            />
          </Box>
        )}

        {/* Create New Neighborhood */}
        {selectedCity && (
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={startCreating}
              disabled={isCreating || isEditing}
              fullWidth
            >
              Create New Neighborhood
            </Button>
          </Box>
        )}

        {/* Neighborhood Creation/Editing Form */}
        {(isCreating || isEditing) && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: isEditing ? 'primary.50' : 'grey.50', border: isEditing ? '2px solid' : 'none', borderColor: 'primary.main' }}>
            <Typography variant="subtitle2" gutterBottom color={isEditing ? 'primary.main' : 'text.primary'}>
              {isCreating ? 'Create Neighborhood' : `Editing: ${selectedNeighborhood?.name}`}
            </Typography>
            
            {isEditing && (
              <Typography variant="caption" color="primary.main" sx={{ display: 'block', mb: 1 }}>
                ⚠️ Editing mode active - map clicks disabled for other neighborhoods
              </Typography>
            )}
            
            <TextField
              fullWidth
              label="Neighborhood Name"
              value={newNeighborhoodName}
              onChange={(e) => setNewNeighborhoodName(e.target.value)}
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Borough</InputLabel>
              <Select
                value={newNeighborhoodBorough}
                onChange={(e) => setNewNeighborhoodBorough(e.target.value)}
                label="Borough"
              >
                {boroughs.map((borough) => (
                  <MenuItem key={borough} value={borough}>
                    {borough}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={adminBoundingBox}
                  onChange={(e) => setAdminBoundingBox(e.target.checked)}
                />
              }
              label="Admin Approved Bounding Box"
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant={isDrawing ? 'contained' : 'outlined'}
                onClick={isDrawing ? stopDrawing : () => {
                  // When editing existing bounds, clear them to draw new ones
                  if (isEditing && editingPath.length > 0) {
                    setEditingPath([]);
                  }
                  startDrawing();
                }}
                size="small"
              >
                {isDrawing 
                  ? 'Stop Drawing' 
                  : (isEditing && editingPath.length > 0 ? 'Draw New Bounds' : 'Draw Bounds')
                }
              </Button>
              <Button
                variant="outlined"
                onClick={clearDrawing}
                size="small"
                disabled={drawingPath.length === 0}
              >
                Clear Drawing
              </Button>
              {isEditing && editingPath.length > 0 && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setEditingPath([]);
                    setError(null);
                  }}
                  size="small"
                  color="warning"
                >
                  Clear Editing
                </Button>
              )}
              {(drawingPath.length > 0 || editingPath.length > 0) && (
                <Button
                  variant="outlined"
                  onClick={recalculateCenter}
                  size="small"
                  color="info"
                  disabled={(isEditing && editingPath.length > 0) ? editingPath.length < 3 : drawingPath.length < 3}
                >
                  Recalculate Center
                </Button>
              )}
              {customCenter && (
                <Button
                  variant="outlined"
                  onClick={resetCenter}
                  size="small"
                  color="secondary"
                >
                  Reset Center
                </Button>
              )}
            </Box>

            {(drawingPath.length > 0 || editingPath.length > 0) && (
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                {isEditing && editingPath.length > 0 
                  ? `Editing points: ${editingPath.length}` 
                  : `Points drawn: ${drawingPath.length}`}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={saveNeighborhood}
                disabled={!newNeighborhoodName.trim() || ((isEditing && editingPath.length > 0) ? editingPath.length < 3 : drawingPath.length < 3)}
              >
                Save
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={cancelEditing}
              >
                Cancel
              </Button>
            </Box>
          </Paper>
        )}

        {/* Neighborhood List */}
        {selectedCity && neighborhoods.length > 0 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">
                Neighborhoods ({neighborhoods.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                <Button size="small" onClick={showAllNeighborhoods}>Show All</Button>
                <Button size="small" onClick={showAllApproved} color="primary">Show Approved</Button>
                <Button size="small" onClick={hideAllNeighborhoods}>Hide All</Button>
              </Box>
            </Box>

            <List dense ref={neighborhoodListRef}>
              {neighborhoods.map((neighborhood) => (
                <ListItem 
                  key={neighborhood._id} 
                  disablePadding
                  data-neighborhood-id={neighborhood._id}
                >
                  <ListItemButton
                    selected={selectedNeighborhood?._id === neighborhood._id}
                    onClick={() => setSelectedNeighborhood(neighborhood)}
                  >
                    <ListItemText 
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {neighborhood.name}
                          {neighborhood.adminBoundingBox && (
                            <Chip 
                              label="Admin" 
                              size="small" 
                              color="primary" 
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" display="block">
                            {neighborhood.borough || 'No borough'}
                          </Typography>
                          <Typography variant="caption" display="block">
                            {neighborhood.boundingBox ? 'Has bounds' : 'No bounds'}
                          </Typography>
                        </Box>
                      }
                    />
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title={visibleNeighborhoods.has(neighborhood._id) ? 'Hide' : 'Show'}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleNeighborhoodVisibility(neighborhood._id);
                          }}
                        >
                          {visibleNeighborhoods.has(neighborhood._id) ? 
                            <VisibilityIcon fontSize="small" /> : 
                            <VisibilityOffIcon fontSize="small" />
                          }
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditing(neighborhood);
                          }}
                          disabled={isCreating || isEditing}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            confirmDelete(neighborhood);
                          }}
                          disabled={isCreating || isEditing}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {selectedCity && neighborhoods.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No neighborhoods found for this city.
          </Typography>
        )}
      </Paper>

      {/* Main Map Area */}
      <Box sx={{ flexGrow: 1, position: 'relative' }}>
        {/* Status Messages */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ position: 'absolute', top: 16, left: 16, right: 16, zIndex: 1000 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert 
            severity="success" 
            sx={{ position: 'absolute', top: 16, left: 16, right: 16, zIndex: 1000 }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        {/* Drawing Instructions */}
        {isDrawing && (
          <Paper sx={{ position: 'absolute', top: 16, right: 16, p: 2, zIndex: 1000, bgcolor: 'warning.50', border: '2px solid', borderColor: 'warning.main' }}>
            <Typography variant="subtitle2" gutterBottom color="warning.main">
              Drawing Mode Active
            </Typography>
                         <Typography variant="body2" color="warning.dark">
               {isEditing && editingPath.length > 0 
                 ? 'Drag blue points to move them. Click on polygon edges to add new points. Right-click points to delete.'
                 : 'Click on the map to add points. Green circles show snap points from other neighborhoods.'
               }
             </Typography>
            {(isEditing || isCreating) && (
              <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mt: 1 }}>
                {isEditing ? `Editing: ${selectedNeighborhood?.name}` : 'Creating new neighborhood'}
              </Typography>
            )}
          </Paper>
        )}

        {/* Color Legend */}
        {visibleNeighborhoods.size > 0 && (
          <Paper sx={{ position: 'absolute', bottom: 16, right: 16, p: 2, zIndex: 1000 }}>
            <Typography variant="subtitle2" gutterBottom>
              Legend
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 16, 
                  height: 16, 
                  backgroundColor: '#2196f3', 
                  border: '2px solid #ffffff',
                  borderRadius: '50%'
                }} />
                <Typography variant="caption">Selected</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 16, 
                  height: 16, 
                  backgroundColor: '#4caf50', 
                  border: '2px solid #ffffff',
                  borderRadius: '50%'
                }} />
                <Typography variant="caption">Admin Approved</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 16, 
                  height: 16, 
                  backgroundColor: '#ff9800', 
                  border: '2px solid #ffffff',
                  borderRadius: '50%'
                }} />
                <Typography variant="caption">Not Approved</Typography>
              </Box>
              {(isCreating || isEditing) && customCenter && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ 
                    width: 16, 
                    height: 16, 
                    backgroundColor: '#9c27b0', 
                    border: '2px solid #ffffff',
                    borderRadius: '50%'
                  }} />
                  <Typography variant="caption">Draggable Center</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        )}

                 <GoogleMap
           mapContainerStyle={mapContainerStyle}
           center={defaultCenter}
           options={mapOptions}
           onLoad={onLoad}
           onUnmount={onUnmount}
           onClick={handleMapClick}
           onMouseMove={handleMapMouseMove}
         >
          {/* Visible neighborhood polygons - includes both filtered neighborhoods and any visible ones from full list */}
          {locations
            .filter(loc => 
              (loc.level === LocationType.NEIGHBORHOOD || loc.level === 'Neighborhood') &&
              visibleNeighborhoods.has(loc._id) &&
              loc.boundingBox?.coordinates?.[0]
            )
            .map((neighborhood) => {
              const coords = neighborhood.boundingBox!.coordinates![0];
              const path = coords.map(([lng, lat]) => ({ lat, lng }));

              // Different colors based on admin approval status
              const isSelected = selectedNeighborhood?._id === neighborhood._id;
              const isApproved = neighborhood.adminBoundingBox;
              
              let fillColor, strokeColor;
              if (isSelected) {
                fillColor = strokeColor = '#2196f3'; // Blue for selected
              } else if (isApproved) {
                fillColor = strokeColor = '#4caf50'; // Green for approved
              } else {
                fillColor = strokeColor = '#ff9800'; // Orange for non-approved
              }

              return (
                                  <Polygon
                    key={neighborhood._id}
                    path={path}
                    options={{
                      fillColor,
                      fillOpacity: isSelected ? 0.3 : 0.2,
                      strokeColor,
                      strokeOpacity: 0.8,
                      strokeWeight: isSelected ? 3 : 2,
                      clickable: !isDrawing && !isCreating && !isEditing // Only clickable when not in editing mode
                    }}
                    onClick={() => {
                      // Only allow selection changes when not editing/creating/drawing
                      if (!isDrawing && !isCreating && !isEditing) {
                        setSelectedNeighborhood(neighborhood);
                      }
                    }}
                  />
              );
            })}

          {/* Current drawing path */}
          {drawingPath.length > 0 && (
            <>
              {/* Polygon if we have enough points */}
              {drawingPath.length >= 3 && (
                <Polygon
                  path={drawingPath}
                  options={{
                    fillColor: '#ff9800',
                    fillOpacity: 0.3,
                    strokeColor: '#ff9800',
                    strokeOpacity: 1,
                    strokeWeight: 3,
                    clickable: false
                  }}
                />
              )}

              {/* Markers for each point */}
              {drawingPath.map((point, index) => (
                <Marker
                  key={index}
                  position={point}
                  options={{
                    icon: {
                      path: google.maps.SymbolPath.CIRCLE,
                      scale: 6,
                      fillColor: '#ff9800',
                      fillOpacity: 1,
                      strokeColor: '#ffffff',
                      strokeWeight: 2
                    }
                  }}
                />
              ))}
            </>
          )}

          {/* Editing path with draggable points */}
          {isEditing && editingPath.length > 0 && (
            <>
              {/* Polygon for editing */}
              {editingPath.length >= 3 && (
                <Polygon
                  path={editingPath}
                  options={{
                    fillColor: '#2196f3',
                    fillOpacity: 0.3,
                    strokeColor: '#2196f3',
                    strokeOpacity: 0.8,
                    strokeWeight: 3,
                    clickable: true
                  }}
                  onClick={addPointToEditingPath}
                />
              )}

              {/* Draggable markers for each editing point */}
              {editingPath.map((point, index) => (
                <Marker
                  key={`edit-${index}`}
                  position={point}
                  draggable={true}
                  onDragStart={handlePointDragStart}
                  onDragEnd={(e) => {
                    if (e.latLng) {
                      handlePointDragEnd(index, {
                        lat: e.latLng.lat(),
                        lng: e.latLng.lng()
                      });
                    }
                  }}
                  options={{
                    icon: {
                      path: google.maps.SymbolPath.CIRCLE,
                      scale: 8,
                      fillColor: '#2196f3',
                      fillOpacity: 1,
                      strokeColor: '#ffffff',
                      strokeWeight: 2
                    }
                  }}
                  title={`Drag to move point ${index + 1} | Right-click to delete`}
                  onRightClick={() => removePointFromEditingPath(index)}
                />
              ))}
            </>
          )}

          {/* Snap point indicator */}
          {snapPoint && isNearSnapPoint && isDrawing && (
            <Marker
              position={snapPoint}
              options={{
                icon: {
                  path: google.maps.SymbolPath.CIRCLE,
                  scale: 12,
                  fillColor: '#00ff00',
                  fillOpacity: 0.8,
                  strokeColor: '#ffffff',
                  strokeWeight: 3
                },
                zIndex: 9999
              }}
              title="Snap to existing point"
            />
          )}

          {/* Center markers for visible neighborhoods */}
          {locations
            .filter(loc => 
              (loc.level === LocationType.NEIGHBORHOOD || loc.level === 'Neighborhood') &&
              visibleNeighborhoods.has(loc._id) &&
              loc.coordinates?.coordinates
            )
            .map((neighborhood) => {
              const [lng, lat] = neighborhood.coordinates!.coordinates;
              const isSelected = selectedNeighborhood?._id === neighborhood._id;
              const isApproved = neighborhood.adminBoundingBox;
              
              let fillColor;
              if (isSelected) {
                fillColor = '#2196f3'; // Blue for selected
              } else if (isApproved) {
                fillColor = '#4caf50'; // Green for approved
              } else {
                fillColor = '#ff9800'; // Orange for non-approved
              }

              return (
                                  <Marker
                    key={`center-${neighborhood._id}`}
                    position={{ lat, lng }}
                    options={{
                      icon: {
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: isSelected ? 10 : 8,
                        fillColor,
                        fillOpacity: 1,
                        strokeColor: '#ffffff',
                        strokeWeight: 2
                      }
                    }}
                    title={`${neighborhood.name}${isApproved ? ' (Admin Approved)' : ''}`}
                    onClick={() => {
                      // Only allow selection changes when not editing/creating/drawing
                      if (!isDrawing && !isCreating && !isEditing) {
                        setSelectedNeighborhood(neighborhood);
                      }
                    }}
                  />
              );
            })}

          {/* Custom draggable center point for editing */}
          {(isCreating || isEditing) && customCenter && (
            <Marker
              position={customCenter}
              draggable={true}
              onDragStart={handleCenterDragStart}
              onDragEnd={handleCenterDragEnd}
              options={{
                icon: {
                  path: google.maps.SymbolPath.CIRCLE,
                  scale: 12,
                  fillColor: isDraggingCenter ? '#ff5722' : '#9c27b0', // Purple, red when dragging
                  fillOpacity: 1,
                  strokeColor: '#ffffff',
                  strokeWeight: 3
                },
                zIndex: 10000 // Ensure it's on top
              }}
              title="Drag to reposition center point"
            />
          )}
        </GoogleMap>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>Delete Neighborhood</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{neighborhoodToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NeighborhoodEditor;
